import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Download, X } from 'lucide-react';

const InstallPWA = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone || 
                       document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Listen for beforeinstallprompt event (Android/Desktop)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Check if user has dismissed before
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed && !standalone) {
        setShowInstallBanner(true);
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Show iOS banner if not dismissed
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-install-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowInstallBanner(true), 3000);
      }
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
        console.log('Khalsa Sidak: App installed');
      }
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    }
  };

  const handleDismiss = () => {
    setShowInstallBanner(false);
    localStorage.setItem('pwa-install-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone || !showInstallBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 animate-in slide-in-from-bottom-5">
      <div className="bg-white rounded-2xl shadow-2xl border border-saffron-200 p-4">
        <div className="flex items-start gap-3">
          {/* App Icon */}
          <div className="w-14 h-14 bg-gradient-to-br from-saffron-500 to-orange-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <span className="text-white text-2xl font-bold">ੴ</span>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-heading font-bold text-gray-900">Install Khalsa Sidak</h3>
            <p className="text-sm text-gray-600 mt-0.5">
              {isIOS 
                ? 'Tap the Share button and "Add to Home Screen"'
                : 'Install our app for a better experience'
              }
            </p>
            
            {!isIOS && (
              <div className="flex gap-2 mt-3">
                <Button 
                  onClick={handleInstallClick}
                  className="bg-saffron-500 hover:bg-saffron-600 text-white text-sm px-4 py-2 h-auto"
                >
                  <Download className="w-4 h-4 mr-1.5" />
                  Install
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={handleDismiss}
                  className="text-gray-500 text-sm px-3 py-2 h-auto"
                >
                  Not now
                </Button>
              </div>
            )}
            
            {isIOS && (
              <Button 
                variant="ghost" 
                onClick={handleDismiss}
                className="text-gray-500 text-sm px-0 py-2 h-auto mt-2"
              >
                Got it
              </Button>
            )}
          </div>
          
          {/* Close button */}
          <button 
            onClick={handleDismiss}
            className="text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default InstallPWA;
